import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { usePopper } from "hooks/usePopper";
import { useToast } from "hooks/useToast";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { setIsUpdateHistory } from "state/slices/transferYourBusiness";
import BeneficialOwners from "./BeneficialOwners";
import RegisteredAddress from "./RegisteredAddress";
import ShareInformation from "./ShareInformation";
import StakeholderDetails from "./StakeholderDetails";
import Status from "./Status";
import Summary from "./Summary";
import _get from "lodash/get";
import _has from "lodash/has";
import step1Validation from "components/PLCTABS/CompanyFormationTab/step1Validation";
import step2Validation from "components/PLCTABS/CompanyFormationTab/step2Validation";
import step3Validation from "components/PLCTABS/CompanyFormationTab/step3Validation";
import registeredOfficeValidation from "components/PLCTABS/RegisteredOfficeAddressTab/registeredOfficeValidation";
import {
  updateCompanyAsync,
  validateCompanyOfficialsAsync,
} from "state/slices/company";
import { validateNominalSharesRestrictions } from "utils/nominalShares";

export default function TransferYourBusinessDetails({ partialData = {} }) {
  const { beneficialOwners, groupedBeneficialOwners } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const applicationState = useSelector((state) => state);

  const { company } = useSelector((state) => state.companySlice);
  const {
    companySlice,
    registeredOfficeSlice,
    sharesTableSlice,
    companyOfficialsSlice,
    beneficialOwnersTableSlice,
  } = applicationState;
  const { companyId } = useParams();

  const [validTabs, setValidTabs] = useState({
    companyFormation: false,
    addRegisteredDetails: false,
    shareInformation: false,
    companyOfficials: false,
    beneficialOwners: false,
  });

  useEffect(() => {
    const tempValidTabs = {
      companyFormation: false,
      addRegisteredDetails: false,
      shareInformation: false,
      companyOfficials: false,
      beneficialOwners: false,
    };
    const checkSidebarNavigationValidation = async () => {
      const isApplicantStepValid = Boolean(
        _has(companySlice, "company.addCoApplicants") &&
          companySlice.company?.applicantCapacity
      );
      tempValidTabs.applicantsDetails = isApplicantStepValid;
      const isStep1Valid = await step1Validation().isValid(
        companySlice.companyFormation.step1
      );
      const isStep2Valid = await step2Validation().schema.isValid(
        companySlice.companyFormation.step2
      );
      const isStep3Valid = await step3Validation().schema.isValid(
        companySlice.companyFormation.step3
      );
      const isCompanyFormationStepValid = true;
      tempValidTabs.companyFormation = isCompanyFormationStepValid;
      const isRegisteredDetailsStepValid =
        isCompanyFormationStepValid &&
        (await registeredOfficeValidation().isValid(registeredOfficeSlice));
      tempValidTabs.addRegisteredDetails =
        isCompanyFormationStepValid && isRegisteredDetailsStepValid;
      const { errors: nominalSharesErrors } = validateNominalSharesRestrictions(
        { shares: sharesTableSlice?.shares }
      );
      const isShareCapitalStepValid =
        isRegisteredDetailsStepValid && nominalSharesErrors.length === 0;
      tempValidTabs.shareInformation = isShareCapitalStepValid;
      const response = await dispatch(
        validateCompanyOfficialsAsync({
          companyId: company?._id,
        })
      );
      const errors = response.payload?.errors ?? [];
      const isCompanyOfficialsStepValid =
        isShareCapitalStepValid && errors.length === 0;
      tempValidTabs.companyOfficials = isCompanyOfficialsStepValid;

      tempValidTabs.beneficialOwners = isCompanyOfficialsStepValid;
      setValidTabs(tempValidTabs);
    };
    if (company?._id) {
      checkSidebarNavigationValidation();
    }
  }, [
    companySlice.companyFormation.companyFormation,
    companySlice?.company,
    companySlice?.company?.addCoApplicants,
    companySlice.company?.applicantCapacity,
    registeredOfficeSlice,
    sharesTableSlice,
    beneficialOwnersTableSlice,
    company?._id,
  ]);

  const tabs = useMemo(() => {
    return [
      {
        id: "companySummary",
        label: "Summary",
        disabled: false,
      },
      {
        id: "registeredAddress",
        label: "Registered Address",
        disabled: !validTabs.companyFormation,
      },
      {
        id: "shareInformation",
        label: "Share information",
        disabled: !validTabs.addRegisteredDetails,
      },
      {
        id: "stakeholderDetails",
        label: "Stakeholder details",
        disabled: !validTabs.shareInformation,
      },

      {
        id: "beneficialOwnership",
        label: "Beneficial ownership",
        disabled: !validTabs.companyOfficials,
      },
      {
        id: "status",
        label: "Status",
        disabled: !validTabs.beneficialOwners,
      },
    ].filter(Boolean);
  }, [validTabs]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const isLastStep = selectedTabIndex === tabs.length - 1;
  const isFirstStep = selectedTabIndex === 0;
  const [agreeToSubmit, setAgreeToSubmit] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToSubmitToReview, setAgreeToSubmitToReview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex]);

  const goToNextTab = () => {
    if (!isLastStep) {
      setSelectedTabIndex(selectedTabIndex + 1);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  };

  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex]);

  /** Click event handlers for history and review */
  const handleSubmitToHistory = () => {
    setIsSubmitting(true);
    // Logic to submit to history
    dispatch(setIsUpdateHistory(true));
    history.push(`/companies/${companyId}/dashboard?history`);
    setIsSubmitting(false);
  };
  const handleSubmitToReview = async () => {
    setIsSubmitting(true);
    // Logic to submit to review
    const response = await dispatch(
      updateCompanyAsync({
        id: companyId,
        data: {
          status: Constants.COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW,
        },
      })
    );

    if (response.payload._id) {
      setIsSubmitting(false);
      toast("success", "Company submitted for review successfully");
      history.push("/");
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={companyId ? setSelectedTabIndex : undefined}
          showTooltip
        />
      </div>
      <div>
        {selectedTab.id === "companySummary" && (
          <Summary partialData={partialData} goToNextTab={goToNextTab} />
        )}
        {selectedTab.id === "registeredAddress" && (
          <RegisteredAddress
            handleNextBtn={goToNextTab}
            handleBackBtn={goToPreviousStep}
            partialData={partialData}
          />
        )}
        {selectedTab.id === "shareInformation" && (
          <ShareInformation
            handleNextBtn={goToNextTab}
            handleBackBtn={goToPreviousStep}
            partialData={partialData}
          />
        )}
        {selectedTab.id === "stakeholderDetails" && (
          <StakeholderDetails
            handleNextBtn={goToNextTab}
            handleBackBtn={goToPreviousStep}
            partialData={partialData}
          />
        )}
        {selectedTab.id === "beneficialOwnership" && (
          <BeneficialOwners
            handleNextBtn={goToNextTab}
            handleBackBtn={goToPreviousStep}
            partialData={partialData}
          />
        )}
        {selectedTab.id === "status" && (
          <Status
            submitToLabel={"BRS"}
            setDisableSubmitBtn={() => {}}
            agreeToSubmit={agreeToSubmit}
            agreeToTerms={agreeToTerms}
            setAgreeToSubmit={setAgreeToSubmit}
            setAgreeToTerms={setAgreeToTerms}
          />
        )}
      </div>
      {![
        "registeredAddress",
        "shareInformation",
        "stakeholderDetails",
        "beneficialOwnership",
      ].includes(selectedTab.id) &&
        !isFirstStep && (
          <div className="flex justify-between py-3">
            <Button
              onClick={goToPreviousStep}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Go back
            </Button>
            <div>
              {!isLastStep ? (
                <Button
                  onClick={goToNextTab}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:cursor-not-allowed"
                >
                  Next Step
                </Button>
              ) : (
                <div className={`flex flex-row gap-0.5`}>
                  <button
                    type="submit"
                    onClick={handleSubmitToReview}
                    className={`flex px-2 py-2 button button-hover button-disabled button-outline button-focus
                      ${
                        !agreeToSubmit || !agreeToTerms || isSubmitting
                          ? "hover:bg-gray-400 hover:shadow-none cursor-not-allowed bg-gray-500 text-white"
                          : `hover:bg-green-600 hover:shadow-lg text-white bg-sflPrimary`
                      }
                       rounded-l-md rounded-r-none
                                                  `}
                    disabled={!agreeToSubmit || !agreeToTerms || isSubmitting}
                  >
                    Submit for Review
                  </button>
                  <Menu as="div">
                    <Menu.Button
                      ref={trigger}
                      className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                              
                                              ${
                                                !agreeToSubmit ||
                                                !agreeToTerms ||
                                                isSubmitting
                                                  ? "hover:bg-gray-400 hover:shadow-none cursor-not-allowed bg-gray-500"
                                                  : `hover:bg-green-600 hover:shadow-lg bg-sflPrimary`
                                              } rounded-l-none rounded-r-md
                                                    `}
                      disabled={!agreeToSubmit || !agreeToTerms || isSubmitting}
                    >
                      <span className="sr-only">Open options</span>
                      <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                    </Menu.Button>
                    <div ref={container} className="z-50">
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items>
                          <div className="py-1">
                            <div>
                              <Menu.Item>
                                {({ active }) => (
                                  <Button
                                    type="submit"
                                    onClick={handleSubmitToHistory}
                                    disabled={isSubmitting}
                                  >
                                    {isSubmitting ? (
                                      <>
                                        <SpinnerIcon />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <div className="ml-1.5 flex items-center">
                                          Proceed To History
                                        </div>
                                      </>
                                    )}
                                  </Button>
                                )}
                              </Menu.Item>
                            </div>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </div>
                  </Menu>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );
}
